"use client";
import { useEffect, useRef } from "react";
import {
  Lock,
  ShieldAlert,
  UserCog,
  Ban,
  Check,
  Route,
} from "lucide-react";

export default function AccuracySection() {
  const stageRef = useRef(null);
  const innerRef = useRef(null);

  useEffect(() => {
    const stage = stageRef.current;
    const inner = innerRef.current;
    if (!stage || !inner) return;

    const rotateAmplitude = 12;
    const scaleOnHover = 1.04;
    let isHovered = false;
    let rotX = 0;
    let rotY = 0;

    const apply = () => {
      inner.style.transform = `rotateX(${rotX.toFixed(2)}deg) rotateY(${rotY.toFixed(2)}deg) scale(${isHovered ? scaleOnHover : 1})`;
    };

    const onMove = (e) => {
      const rect = stage.getBoundingClientRect();
      const offsetX = e.clientX - rect.left - rect.width / 2;
      const offsetY = e.clientY - rect.top - rect.height / 2;
      rotX = (offsetY / (rect.height / 2)) * -rotateAmplitude;
      rotY = (offsetX / (rect.width / 2)) * rotateAmplitude;
      apply();
    };
    const onEnter = () => {
      isHovered = true;
      apply();
    };
    const onLeave = () => {
      isHovered = false;
      rotX = 0;
      rotY = 0;
      apply();
    };

    stage.addEventListener("mousemove", onMove);
    stage.addEventListener("mouseenter", onEnter);
    stage.addEventListener("mouseleave", onLeave);
    return () => {
      stage.removeEventListener("mousemove", onMove);
      stage.removeEventListener("mouseenter", onEnter);
      stage.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <section id="product" className="section-pad bg-purple-glow relative overflow-hidden">
      <div className="container-c relative">
        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <div ref={stageRef} className="tilted-card">
              <div ref={innerRef} className="tilted-card-inner rounded-3xl border-2 border-purple/30 bg-white p-2 shadow-card-hover">
                <div className="rounded-2xl border border-line bg-white">
                  <div className="flex items-center justify-between border-b border-line px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full bg-[#E5484D]/70"></span>
                      <span className="h-2.5 w-2.5 rounded-full bg-[#F4A11E]/70"></span>
                      <span className="h-2.5 w-2.5 rounded-full bg-[#2EBD7E]/70"></span>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] font-medium text-muted">
                      <Lock className="h-3 w-3 text-success" />
                      riskintel.cloudastra.co
                    </div>
                    <div className="w-12"></div>
                  </div>

                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FBE5E6] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-danger">
                            <span className="h-1.5 w-1.5 rounded-full bg-danger"></span> High risk
                          </span>
                          <span className="text-[11px] font-medium text-muted">Match score 96</span>
                        </div>
                        <h4 className="mt-2 font-display text-[20px] font-semibold tracking-tight text-ink">Rohan Verma</h4>
                        <p className="font-mono text-[11px] text-muted">custom_in_mca_disqualified_482731</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted">Screened</p>
                        <p className="mt-1 font-mono text-[11px] text-ink">19 May 2026 · 14:32 IST</p>
                      </div>
                    </div>

                    <div className="mt-5">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted">
                        Aliases <span className="text-blue">(8 sources clustered)</span>
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <span className="rounded-full border border-line bg-surface px-2.5 py-1 text-[12px] font-medium text-ink">Rohan Verma</span>
                        <span className="rounded-full border border-line bg-surface px-2.5 py-1 text-[12px] font-medium text-ink">R. Verma</span>
                        <span className="rounded-full border border-line bg-surface px-2.5 py-1 text-[12px] font-medium text-ink">रोहन वर्मा</span>
                      </div>
                    </div>

                    <div className="mt-5">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted">Matched on</p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FBE5E6] px-2.5 py-1 text-[11px] font-semibold text-danger">
                          <ShieldAlert className="h-3 w-3" /> Sanctions (UAPA)
                        </span>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FEEFD2] px-2.5 py-1 text-[11px] font-semibold text-warning">
                          <UserCog className="h-3 w-3" /> PEP, domestic
                        </span>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FBE5E6] px-2.5 py-1 text-[11px] font-semibold text-danger">
                          <Ban className="h-3 w-3" /> Disqualified director
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <span className="inline-flex items-center gap-1 rounded-md border border-line bg-white px-1.5 py-0.5 font-mono text-[10px] font-semibold text-ink">MCA</span>
                        <span className="inline-flex items-center gap-1 rounded-md border border-line bg-white px-1.5 py-0.5 font-mono text-[10px] font-semibold text-ink">OC</span>
                        <span className="inline-flex items-center gap-1 rounded-md border border-line bg-white px-1.5 py-0.5 font-mono text-[10px] font-semibold text-ink">MCA+OC</span>
                      </div>
                    </div>

                    <div className="mt-5 grid grid-cols-3 gap-3 rounded-xl bg-surface p-4">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">PAN</p>
                        <p className="mt-1 font-mono text-[12px] font-medium text-ink">AXVPV••••2K</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">DIN</p>
                        <p className="mt-1 font-mono text-[12px] font-medium text-ink">00194827</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">GSTIN</p>
                        <p className="mt-1 font-mono text-[12px] font-medium text-ink">27AXVPV••••2K1Z6</p>
                      </div>
                    </div>

                    <div className="mt-5 rounded-xl border border-line bg-surface p-4">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted">Why this match</p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#E6F7EE] px-2 py-0.5 text-[11px] font-semibold text-success">
                          <Check className="h-3 w-3" /> PAN
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#E6F7EE] px-2 py-0.5 text-[11px] font-semibold text-success">
                          <Check className="h-3 w-3" /> DOB
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#E6F7EE] px-2 py-0.5 text-[11px] font-semibold text-success">
                          <Check className="h-3 w-3" /> Address
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3 text-[12px] text-ink">
                        <Route className="h-3.5 w-3.5 text-danger" />
                        <span>
                          <strong className="font-semibold">Path to sanctioned entity:</strong> 2 hops via shared CIN
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <p className="mt-3 text-center text-[12px] text-muted">
              Illustrative. The live product is on the RiskIntel site.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3 py-1.5 text-xs font-medium text-ink shadow-card">
              <span className="h-2 w-2 rounded-full bg-purple"></span>
              <span className="text-purple font-semibold">A glimpse of RiskIntel</span>
            </div>
            <h2 className="mt-5 font-display text-[30px] leading-[1.08] tracking-tightest text-balance text-ink sm:text-[36px] md:text-[44px]">
              One decision-ready card. Every source, every audit step.
            </h2>
            <p className="mt-4 text-[16px] leading-relaxed text-muted text-pretty">
              This is the card your analyst lives in. The full product is one click away.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
